import { Component, Input, Output, EventEmitter } from '@angular/core';
import { EllipsisMenuItem } from '@gainsystems/gains-ui-commons';

/**
 * Interface for gauge zone configuration
 */
export interface GaugeZone {
  /** Zone label/category */
  label: string;
  /** Color for the zone */
  color: string;
  /** Percentage of gauge this zone occupies (0-100) */
  percentage: number;
}

/**
 * Interface for threshold configuration
 */
export interface GaugeThreshold {
  /** Minimum value for this range */
  min: number;
  /** Maximum value for this range */
  max: number;
  /** Color for this range */
  color: string;
  /** Optional label for this range */
  label?: string;
}

@Component({
  selector: 'gauge-kpi-widget',
  standalone: false,
  templateUrl: './gauge-kpi-widget.component.html',
  styleUrl: './gauge-kpi-widget.component.scss'
})
export class GaugeKpiWidgetComponent {
  @Input() widgetTitle = '';
  @Input() value = 0;
  @Input() description = '';
  @Input() widthClass = 'extra-chart-small';
  @Input() menuItems: EllipsisMenuItem[] = [];
  @Input() widgetId = '';

  /**
   * Threshold configuration for the gauge
   * Example: [
   *   { min: 0, max: 200, color: '#92ad38', label: 'Good' },
   *   { min: 201, max: 400, color: '#f5c542', label: 'Warning' },
   *   { min: 401, max: 600, color: '#c00016', label: 'Critical' }
   * ]
   */
  @Input() thresholds: GaugeThreshold[] = [
    { min: 0, max: 390, color: '#7BA428', label: 'Good' },
    { min: 391, max: 460, color: '#CC9E00', label: 'Warning' },
    { min: 461, max: 600, color: '#c00016', label: 'Critical' }
  ];

  /** Minimum value for the gauge scale */
  @Input() minValue = 0;

  /** Maximum value for the gauge scale */
  @Input() maxValue = 600;

  @Output() changeEvent = new EventEmitter<string>();

  // SVG gauge configuration
  readonly gaugeSize = 200;
  readonly strokeWidth = 32;
  readonly centerX = 100;
  readonly centerY = 100;
  readonly radius = 80;

  handleMenuAction(action: string): void {
    this.changeEvent.emit(action);
  }

  /**
   * Calculate the gauge zones based on thresholds
   */
  getGaugeZones(): GaugeZone[] {
    const totalRange = this.maxValue - this.minValue;
    const zones: GaugeZone[] = [];

    this.thresholds.forEach(threshold => {
      const rangeSize = threshold.max - threshold.min;
      const percentage = (rangeSize / totalRange) * 100;

      zones.push({
        label: threshold.label || '',
        color: threshold.color,
        percentage: percentage
      });
    });

    return zones;
  }

  /**
   * Calculate the SVG path for a gauge arc
   */
  getArcPath(startAngle: number, endAngle: number): string {
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const startX = this.centerX + this.radius * Math.cos(startRad);
    const startY = this.centerY + this.radius * Math.sin(startRad);
    const endX = this.centerX + this.radius * Math.cos(endRad);
    const endY = this.centerY + this.radius * Math.sin(endRad);

    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

    return `M ${startX} ${startY} A ${this.radius} ${this.radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`;
  }

  /**
   * Get gauge arc paths for each zone
   */
  getGaugeArcPaths(): { path: string; color: string }[] {
    const zones = this.getGaugeZones();
    const paths: { path: string; color: string }[] = [];

    let currentAngle = 180; // Start at 180 degrees (left side of semi-circle)

    zones.forEach(zone => {
      const angleRange = (zone.percentage / 100) * 180; // 180 degrees total for semi-circle
      const endAngle = currentAngle + angleRange;

      paths.push({
        path: this.getArcPath(currentAngle, endAngle),
        color: zone.color
      });

      currentAngle = endAngle;
    });

    return paths;
  }

  /**
   * Calculate the needle angle based on current value
   */
  getNeedleAngle(): number {
    const clampedValue = Math.max(this.minValue, Math.min(this.maxValue, this.value));
    const percentage = (clampedValue - this.minValue) / (this.maxValue - this.minValue);
    return 180 + percentage * 180; // 180 to 360 degrees
  }

  /**
   * Get the needle path
   */
  getNeedlePath(): string {
    const angle = this.getNeedleAngle();
    const rad = (angle * Math.PI) / 180;

    const needleLength = this.radius - 10;
    const endX = this.centerX + needleLength * Math.cos(rad);
    const endY = this.centerY + needleLength * Math.sin(rad);

    return `M ${this.centerX} ${this.centerY} L ${endX} ${endY}`;
  }

  /**
   * Get formatted value for display
   */
  getFormattedValue(): string {
    return Math.round(this.value).toString();
  }
}
